import { Box, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router';
import { SDLC_PATH } from '../../config';
import { cardSurfaceSx, interactiveCardSx, pressedCardSx, stageColourFor } from '../../theme';
import type { MethodCardProps } from './types';

/**
 * A method in the grid under a process. The whole card is the link, like the L1
 * phase cards and unlike the process cards, because a method card holds one
 * destination.
 *
 * **Its stage edge is inset rather than flush.** The phase card's bar runs to
 * the card's top corners; this one sits inside the padding, which is what the
 * designs draw and what stops a 4px bar reading as a second border on a card
 * this small.
 *
 * **The designs put "Practitioner Target" in the stage colour at the bottom and
 * this card does not.** `ContentSummary` carries no mastery target — the field
 * is on the full `Method` record, which a listing deliberately does not fetch —
 * so the footer would be a guess. It comes back when the summary carries one.
 */
export const MethodCard = ({ method, reference }: MethodCardProps) => {
  const stageColour = stageColourFor(method.stage, 'onSurface');

  return (
    <RouterLink
      to={`${SDLC_PATH}/${method.path}`}
      style={{ textDecoration: 'none', display: 'flex', height: '100%' }}
    >
      <Box
        sx={{
          ...cardSurfaceSx,
          ...interactiveCardSx,
          ...pressedCardSx(stageColour),
          padding: 6,
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          width: '100%',
        }}
      >
        <Box sx={{ height: 4, backgroundColor: stageColour, flexShrink: 0 }} />

        <Typography
          variant="caption"
          component="span"
          sx={{ fontWeight: 500, letterSpacing: '1px', color: 'text.secondary' }}
        >
          METHOD {reference}
        </Typography>

        <Typography variant="h4" component="h3" sx={{ color: 'text.primary' }}>
          {method.name}
        </Typography>

        {/*
          Clamped to three lines, like the L1 phase card and for the same
          reason: the full text is on the method page, and a grid whose cards
          are as tall as their longest description stops being scannable. How
          many lines fit is a layout question, so it is answered here rather
          than by truncating in the API.
        */}
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 3,
            overflow: 'hidden',
          }}
        >
          {method.description}
        </Typography>
      </Box>
    </RouterLink>
  );
};
